import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, Alert, List, Typography, Progress, Tag, Space } from 'antd';
import {
  DollarOutlined, ShopOutlined, InboxOutlined, CheckCircleOutlined,
  WarningOutlined, SwapOutlined, ApartmentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { analysisService, organizationService } from '../services/api';

const { Title, Text } = Typography;

// Clickable card style
const clickableCardStyle = {
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    total_spend: 0,
    vendor_count: 0,
    product_count: 0,
    potential_savings: 0,
    implemented_savings: 0,
    recommendations: {
      total: 0,
      reviewed: 0,
      implemented: 0,
      by_type: {
        alternatives: 0,
        overlaps: 0
      }
    },
    top_vendors: [],
    contracts_expiring_soon: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dashData, orgData, overlapsData] = await Promise.all([
          analysisService.getDashboardData(),
          organizationService.getMyOrganization().catch(() => null),
          analysisService.getVendorOverlaps().catch(() => null)
        ]);
        // Use consolidation savings from overlaps endpoint so dashboard matches the Consolidation page
        if (overlapsData?.summary?.total_consolidation_savings != null) {
          dashData.potential_savings = overlapsData.summary.total_consolidation_savings;
        }
        setDashboardData(dashData);
        setOrganization(orgData);
        setError(null);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        if (err.response?.status === 400 && err.response?.data?.detail?.includes('organization')) {
          setError('Please set up your organization to view dashboard analytics.');
        } else {
          setError('Failed to load dashboard data. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const savingsRate = dashboardData.potential_savings > 0
    ? Math.round((dashboardData.implemented_savings / dashboardData.potential_savings) * 100)
    : 0;

  const reviewRate = dashboardData.recommendations.total > 0
    ? Math.round((dashboardData.recommendations.reviewed / dashboardData.recommendations.total) * 100)
    : 0;

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div>
      <Title level={2}>Dashboard</Title>
      {organization && (
        <Text type="secondary" style={{ fontSize: 16 }}>
          {organization.name} {organization.industry && `- ${organization.industry}`}
        </Text>
      )}

      <Spin spinning={loading}>
        {/* Key Metrics Row */}
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="dashboard-card"
              hoverable
              style={clickableCardStyle}
              onClick={() => navigate('/spend')}
            >
              <Statistic
                title="Budgeted Annual Spend"
                value={dashboardData.total_spend}
                precision={0}
                valueStyle={{ color: '#3f8600' }}
                prefix={<DollarOutlined />}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="dashboard-card"
              hoverable
              style={clickableCardStyle}
              onClick={() => navigate('/my-vendors')}
            >
              <Statistic
                title="Active Vendors"
                value={dashboardData.vendor_count}
                valueStyle={{ color: '#0050b3' }}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="dashboard-card"
              hoverable
              style={clickableCardStyle}
              onClick={() => navigate('/product-database')}
            >
              <Statistic
                title="Products Tracked"
                value={dashboardData.product_count}
                valueStyle={{ color: '#722ed1' }}
                prefix={<InboxOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              className="dashboard-card"
              hoverable
              style={clickableCardStyle}
              onClick={() => navigate('/recommendations')}
            >
              <Statistic
                title="Potential Savings"
                value={dashboardData.potential_savings}
                precision={0}
                valueStyle={{ color: '#cf1322' }}
                prefix={<DollarOutlined />}
                formatter={(value) => `$${value.toLocaleString()}`}
              />
            </Card>
          </Col>
        </Row>

        {/* Recommendations & Savings Row */}
        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Recommendations Overview"
              style={{ marginBottom: 24, ...clickableCardStyle }}
              hoverable
              onClick={() => navigate('/recommendations')}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Total"
                    value={dashboardData.recommendations.total}
                    valueStyle={{ fontSize: '24px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Reviewed"
                    value={dashboardData.recommendations.reviewed}
                    valueStyle={{ fontSize: '24px', color: '#1890ff' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Implemented"
                    value={dashboardData.recommendations.implemented}
                    valueStyle={{ fontSize: '24px', color: '#52c41a' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
              </Row>

              {/* Recommendation Types */}
              <Row gutter={16} style={{ marginTop: 20 }}>
                <Col span={12}>
                  <Card
                    size="small"
                    style={{ background: '#f0f5ff', cursor: 'pointer' }}
                    hoverable
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/alternatives');
                    }}
                  >
                    <Space>
                      <SwapOutlined style={{ color: '#1890ff' }} />
                      <div>
                        <Text type="secondary">Alternatives</Text>
                        <div style={{ fontSize: 20, fontWeight: 500 }}>
                          {dashboardData.recommendations.by_type?.alternatives || 0}
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    size="small"
                    style={{ background: '#fff7e6', cursor: 'pointer' }}
                    hoverable
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/recommendations');
                    }}
                  >
                    <Space>
                      <ApartmentOutlined style={{ color: '#fa8c16' }} />
                      <div>
                        <Text type="secondary">Overlap Recommendations</Text>
                        <div style={{ fontSize: 20, fontWeight: 500 }}>
                          {dashboardData.recommendations.by_type?.overlaps || 0}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {dashboardData.product_count} total products
                        </Text>
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>

              {/* Progress Bars */}
              <div style={{ marginTop: 20 }}>
                <Text>Review Progress</Text>
                <Progress percent={reviewRate} status="active" />
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title="Savings Progress"
              style={{ marginBottom: 24, ...clickableCardStyle }}
              hoverable
              onClick={() => navigate('/recommendations')}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Implemented Savings"
                    value={dashboardData.implemented_savings}
                    precision={0}
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<DollarOutlined />}
                    formatter={(value) => `$${value.toLocaleString()}`}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Remaining Opportunity"
                    value={dashboardData.potential_savings - dashboardData.implemented_savings}
                    precision={0}
                    valueStyle={{ color: '#faad14' }}
                    prefix={<DollarOutlined />}
                    formatter={(value) => `$${Math.max(0, value).toLocaleString()}`}
                  />
                </Col>
              </Row>

              <div style={{ marginTop: 20 }}>
                <Text>Savings Capture Rate</Text>
                <Progress
                  percent={savingsRate}
                  status={savingsRate === 100 ? 'success' : 'active'}
                  strokeColor={{
                    '0%': '#108ee9',
                    '100%': '#87d068',
                  }}
                />
              </div>

              {/* Contracts Expiring Alert */}
              {dashboardData.contracts_expiring_soon > 0 && (
                <Alert
                  message={`${dashboardData.contracts_expiring_soon} contract(s) expiring within 90 days`}
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginTop: 20, cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/my-vendors?filter=expiring');
                  }}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Top Vendors */}
        <Row gutter={16}>
          <Col span={24}>
            <Card
              title="Top Vendors by Spend"
              extra={<a onClick={() => navigate('/my-vendors')}>View All</a>}
            >
              {dashboardData.top_vendors && dashboardData.top_vendors.length > 0 ? (
                <List
                  grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 5 }}
                  dataSource={dashboardData.top_vendors}
                  renderItem={(vendor, index) => (
                    <List.Item>
                      <Card
                        size="small"
                        hoverable
                        style={clickableCardStyle}
                        onClick={() => navigate('/my-vendors')}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Space>
                            <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : 'default'}>
                              #{index + 1}
                            </Tag>
                            <Text strong>{vendor.name}</Text>
                          </Space>
                          <Statistic
                            value={vendor.spend}
                            precision={0}
                            prefix="$"
                            valueStyle={{ fontSize: 16 }}
                          />
                        </Space>
                      </Card>
                    </List.Item>
                  )}
                />
              ) : (
                <p>No vendor data available. Add vendors to your portfolio to see spending analytics.</p>
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Dashboard;
